import mergeDeep from 'lodash.merge';
import { jsonStringify } from '@ringcentral/rwc-utils/src/lang/json';
import { LoggerFactory } from './logger-factory';
import { ConfigStorage } from './config-storage';
import { filterObj, mergeWithDefault, isBrowser, downloadFile, mapObjPromised } from './utils';
import { DEFAULT_RUNTIME_CONFIG } from './default-config';
import {
    mapConfigToLoggerOpts,
    mapLoggerOptsToConfig,
    pickTransportOpts,
    getTransportLevels,
    storeNames,
} from './config-mappers';
import './types';
import { nanoid } from 'nanoid';

export class RwcLogger {
    /**
     * @param {Partial<DEFAULT_RUNTIME_CONFIG>} [initialOpts]
     * @param {{ desktopLog: (void | null | Function), emit: (void | null | Function), storage?: object, detectSensitiveData?: Function, removeSensitiveData?: Function }} [externalAPI]
     */
    constructor(initialOpts = {}, externalAPI = {}) {
        this.initialOpts = initialOpts;
        this.configStorage = new ConfigStorage(externalAPI);
        const config = mergeWithDefault(
            DEFAULT_RUNTIME_CONFIG,
            mergeDeep({}, initialOpts, this.configStorage.getConfig())
        );

        /** RwcLogger instance id, useful for logs filtering when multiple tabs write logs to the same storage at the same time */
        this.instId = nanoid(4); // 64 ^ 4 = 16_777_216 permutations

        const format = (lvl, prefix, msgParts) => {
            const res = LoggerFactory.defaults.format(lvl, prefix, msgParts);
            res.inst = this.instId;
            return res;
        };

        this.logFactory = new LoggerFactory({
            format,
            ...mapConfigToLoggerOpts(config, externalAPI),
        });
        this.byPrefix = this.logFactory.byPrefix;
        this.removeSensitiveData = externalAPI.removeSensitiveData;
    }

    /** @returns {LoggerFactory} logger factory instance */
    getLogFactory = () => this.logFactory;

    getDefaultConfig = () => DEFAULT_RUNTIME_CONFIG;

    /** @returns {Object} currently used logger configuration */
    getConfig = () => mapLoggerOptsToConfig(this.logFactory);

    /** @returns {Object} currently stored logger configuration */
    getStorageConfig = () => this.configStorage.getConfig();

    /** @returns {boolean} was the local storage configuration updated */
    isStorageConfigEmpty = () => this.configStorage.isEmpty();

    /** Deletes config from the local storage, resets runtime configuration to initial state */
    reset = () => {
        const initialConfig = mergeWithDefault(DEFAULT_RUNTIME_CONFIG, this.initialOpts);
        this.applyConfig(initialConfig, true);
        this.configStorage.clearConfig();
    };

    /**
     * Applies passed runtime configuration to logFactory
     * @param {Object}
     * @param {boolean} [isTemporary=false] determines whether to save given config changes to the local storage
     */
    applyConfig = (opts = {}, isTemporary = false) => {
        const { level, transports, prefixes } = opts;
        if (transports) {
            Object.entries(this.logFactory.getTransports()).forEach(([name, transport]) => {
                const filteredOpts = pickTransportOpts(name, transports[name]);
                transport.applyRuntimeOpts(filteredOpts);
            });
        }

        const transportLevels = getTransportLevels(transports);
        this.logFactory.applyRuntimeOpts({ level, prefixes, transportLevels });

        if (!isTemporary) {
            this.configStorage.updateConfig(opts);
        }
    };

    /**
     * Sets global logging level
     * @param {string} level - level name
     * @param {boolean} [isTemporary=false] determines whether to save given config changes to the local storage
     */
    setLevel = (level, isTemporary = false) => {
        this.logFactory.applyRuntimeOpts({ level });
        if (!isTemporary) {
            this.configStorage.updateConfig({ level });
        }
    };

    /**
     * Applies passed transport configuration to transport instance
     * @param {string} name - transport name
     * @param {Object} config - transport runtime configuration
     * @param {boolean} [isTemporary=false] determines whether to save given config changes to the local storage
     */
    applyTransportConfig = (name, config, isTemporary = false) => {
        const transport = this.logFactory.getTransport(name);
        if (!transport) return;

        const filtered = pickTransportOpts(name, config);
        transport.applyRuntimeOpts(filtered);

        if (config.level) {
            this.logFactory.setTransportLevels({ [name]: config.level });
        }

        if (!isTemporary) {
            this.configStorage.updateTransportConfig(name, config);
        }
    };

    /**
     * Sets level for specified transport
     * @param {string} name - transport name
     * @param {string} level - level name
     * @param {boolean} [isTemporary=false] determines whether to save given config changes to the local storage
     */
    setTransportLevel = (name, level, isTemporary = false) => {
        this.logFactory.setTransportLevels({ [name]: level });
        if (!isTemporary) {
            this.configStorage.updateTransportConfig(name, { level });
        }
    };

    /**
     * @param {string} prefixes - a pattern that maximizes priority of matched prefixes
     * @param {boolean} [isTemporary=false] determines whether to save given config changes to the local storage
     */
    setPrefixes = (prefixes, isTemporary = false) => {
        this.logFactory.setPrefixes(prefixes);
        if (!isTemporary) {
            this.configStorage.updateConfig({ prefixes });
        }
    };

    /**
     * Adds the given prefixes to the existing ones
     * @param {string} prefixes - a pattern that maximizes priority of matched prefixes
     * @param {boolean} [isTemporary=false] determines whether to save given config changes to the local storage
     */
    addPrefixes = (prefixes, isTemporary = false) => {
        this.setPrefixes(
            [this.getConfig().prefixes, prefixes].filter(Boolean).join(' '),
            isTemporary
        );
    };

    getSnapshots = () => this.logFactory.getSnapshots();

    /**
     * Downloads stored logs to a file for specified level list (all by default)
     * @param {string[]} [levels]
     */
    downloadLogs = levels => {
        if (!isBrowser()) return;

        const downloadLogs = logsAndSnapshots => {
            const url = URL.createObjectURL(
                new Blob([jsonStringify(logsAndSnapshots)], { type: 'application/json' })
            );
            downloadFile(url, `rwc-logs-${new Date().toISOString()}.json`);
            URL.revokeObjectURL(url);
        };

        this.getLogs(levels).then(downloadLogs);
    };

    /**
     * get stored logs to a file for specified level list (all by default) and period (all by default)
     * @param {string[]|undefined} levels - ['error'|'warn'|'info'|'debug']
     * @param {Date|undefined} [startTime] - from Date
     * @param {Date|undefined} [endTime] - to Date
     */
    getLogs = async (levels, startTime, endTime) => {
        if (!isBrowser()) return;

        const getTransportLogs = (name, transport) =>
            transport.getLogs?.(levels || storeNames, startTime, endTime);
        const filterTransportLogs = (name, logs) => Boolean(logs);

        const [logs, snapshots] = await Promise.all([
            mapObjPromised(getTransportLogs, this.logFactory.getTransports())
                .then(allTransportLogs => filterObj(filterTransportLogs, allTransportLogs))
                .catch(err => this.byPrefix('rwc-logger').error('Failed to download logs.', err)),
            this.getSnapshots().catch(err =>
                this.byPrefix('rwc-logger').error('Failed to get snapshots.', err)
            ),
        ]);
        // In order to keep compatibility with previous log,
        // logs that grouped by transports are spread on the first level of log
        const groupedLogs = {
            ...logs,
            snapshots,
        };

        if (typeof this.removeSensitiveData === 'function') {
            return this.removeSensitiveData(groupedLogs, levels?.includes('debug'));
        }

        return groupedLogs;
    };

    /**
     * Clears idb logs by specified levels (all levels by default)
     * @param {string[]} [levels]
     */
    clearLogs(levels) {
        return mapObjPromised(
            (name, transport) => transport.clearLogs?.(levels || storeNames),
            this.logFactory.getTransports()
        ).catch(err => this.byPrefix('rwc-logger').error('Failed to clear logs.', err));
    }
}

export const labels = {
    REACT_ERROR: 'REACT_ERROR',
    UNCAUGHT_ERROR: 'UNCAUGHT_ERROR',
    HANDLED_REJECTION: 'HANDLED_REJECTION',
    UNHANDLED_REJECTION: 'UNHANDLED_REJECTION',
};
